/**
 * wallUtils.js
 * ----------------------------------------------------------------------------
 * PAREDE: elemento de referência visual (menu "Utilidades" da toolbar) que
 * demarca o formato do ambiente/encaixe da peça. NÃO é insumo, NÃO entra na
 * ficha_tecnica nem no orçamento — vive só em `drawingExtras.elements` (igual
 * a linha/seta/anotação), por isso reaproveita addEntity/onMoveEntity etc.
 * sem precisar de nenhuma persistência nova.
 *
 * Geometricamente é um caminho de N pontos-âncora, com UMA curva quadrática
 * (bezier) por segmento: o "bow" é o deslocamento perpendicular do ponto de
 * controle a partir do meio do segmento — arrastar o bow entorta aquele
 * trecho da parede (paredes raramente são 100% retas/perpendiculares). Com
 * bow=0 o segmento é uma reta pura (o ponto de controle cai exatamente no
 * meio dos dois pontos, o que degenera a quadrática numa reta).
 *
 * A FAIXA (espessura real de alvenaria) é desenhada como um traço grosso em
 * cima do próprio `getWallPathD` com `stroke-linejoin: miter` — o SVG nativo
 * já faz o corte da quina em bico reto, sem precisar de geometria de offset
 * manual (que arredondava/entortava as quinas de 90°). A TEXTURA de tijolo
 * vem de um `<pattern>` do SVG usado como paint do próprio traço (ver
 * `stdBrickPattern` em DrawingCanvas.vue) — não precisa de geometria própria
 * aqui, o navegador cuida do ladrilhamento.
 */

import { uid } from "./annotationUtils";

/** Espessura visual de uma parede de alvenaria comum (metros). */
export const WALL_THICKNESS_M = 0.15;

/** Cria o elemento de parede a partir dos pontos coletados no desenho. */
export function createWallElement(points) {
  const pts = (points || []).map(p => ({ x: p.x, y: p.y }));
  return {
    id: uid("wall"),
    type: "wall",
    shape: "bezier",
    label: "Parede",
    points: pts,
    bows: new Array(Math.max(0, pts.length - 1)).fill(0),
    angles: computeWallAngles(pts),
    x: pts[0] ? pts[0].x : 0,
    y: pts[0] ? pts[0].y : 0,
    style: {},
    metadata: {}
  };
}

/** Ponto de controle de UM segmento: meio do segmento + normal*bow. */
export function segmentControlPoint(p0, p1, bow) {
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const amount = Number(bow) || 0;
  return { x: (p0.x + p1.x) / 2 + nx * amount, y: (p0.y + p1.y) / 2 + ny * amount };
}

/** Path SVG (atributo `d`) do contorno da parede — 1 quadrática por segmento. */
export function getWallPathD(points, bows) {
  if (!points || points.length < 2) return "";
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const c = segmentControlPoint(points[i], points[i + 1], (bows || [])[i]);
    d += ` Q ${c.x} ${c.y} ${points[i + 1].x} ${points[i + 1].y}`;
  }
  return d;
}

/** Pontos de alça (âncoras + controle de curvatura por segmento) p/ edição. */
export function getWallHandles(points, bows) {
  const pts = points || [];
  const anchors = pts.map((p, i) => ({ index: i, x: p.x, y: p.y }));
  const bowHandles = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const c = segmentControlPoint(pts[i], pts[i + 1], (bows || [])[i]);
    bowHandles.push({ index: i, x: c.x, y: c.y });
  }
  return { anchors, bowHandles };
}

/**
 * Ângulo (em graus, 0–180) em cada vértice INTERNO da parede — onde dois
 * trechos se encontram. 180° = trecho reto (sem curva); menor que isso =
 * quanto mais fechada é a "quina". Cada entrada já traz a posição para a
 * etiqueta (deslocada para o lado de "dentro" do ângulo, na bissetriz).
 */
export function computeWallAngles(points) {
  const pts = points || [];
  const out = [];
  for (let i = 1; i < pts.length - 1; i++) {
    const p = pts[i];
    const a = pts[i - 1];
    const b = pts[i + 1];
    const ax = a.x - p.x, ay = a.y - p.y;
    const bx = b.x - p.x, by = b.y - p.y;
    const alen = Math.hypot(ax, ay) || 1;
    const blen = Math.hypot(bx, by) || 1;
    const uax = ax / alen, uay = ay / alen;
    const ubx = bx / blen, uby = by / blen;
    const dot = uax * ubx + uay * uby;
    const det = uax * uby - uay * ubx;
    const deg = Math.abs(Math.atan2(det, dot)) * (180 / Math.PI);
    let bisx = uax + ubx;
    let bisy = uay + uby;
    const bisLen = Math.hypot(bisx, bisy);
    if (bisLen < 1e-3) {
      // vetores quase opostos (parede reta): usa a normal do trecho
      bisx = -uay; bisy = uax;
    } else {
      bisx /= bisLen; bisy /= bisLen;
    }
    out.push({ index: i, deg, x: p.x, y: p.y, dirX: bisx, dirY: bisy });
  }
  return out;
}
